import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { CLIENT_CODES } from "../services/financeCore/clientTaxProfileService";
import { WHT_MODELS } from "../services/financeCore/whtCalculator";
import { createManualInvoiceSnapshot, getInvoiceUsage, listManualEntries, updateManualInvoiceSnapshot } from "../services/financeCore/manualSnapshotService";
import { descriptionMonths, formFromSnapshot } from "../services/financeCore/manualSnapshotLogic";

const CLIENT_LIST = Object.values(CLIENT_CODES);
const LAST_CLIENT_KEY = "financeSelectedClient";
const money = (value) => `PKR ${Math.round(Number(value || 0)).toLocaleString("en-PK")}`;

function emptyForm(clientCode) {
  return {
    clientCode,
    invoiceNumber: "",
    invoiceDate: new Date().toISOString().slice(0, 10),
    accountingPeriod: new Date().toISOString().slice(0, 7),
    serviceChargeSubtotal: "",
    gstAmount: "",
    grossAmount: "",
    whtAmount: "",
    gstWithheldAmount: "",
    recoveryAddBack: "",
    whtModelUsed: "",
    reason: ""
  };
}

export default function ManualInvoiceEntry() {
  const [clientCode, setClientCode] = useState(() => {
    const saved = localStorage.getItem(LAST_CLIENT_KEY);
    return CLIENT_LIST.includes(saved) ? saved : CLIENT_LIST[0];
  });
  const [form, setForm] = useState(() => emptyForm(clientCode));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // { invoiceNumber, usage }
  const [changeReason, setChangeReason] = useState("");
  const [periodChosen, setPeriodChosen] = useState(false); // true once the operator picks the month themselves

  const refresh = async (code) => {
    setLoading(true);
    try {
      setEntries(await listManualEntries(code));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(clientCode); }, [clientCode]);

  const cancelEdit = () => { setEditing(null); setChangeReason(""); setPeriodChosen(false); setForm(emptyForm(clientCode)); };

  const startEdit = async (entry) => {
    setError(""); setMessage("");
    let usage = { allocationCount: 0, allocatedAmount: 0, hasWhtRecord: false };
    try { usage = await getInvoiceUsage(entry.invoiceNumber); } catch (err) { /* the edit still works; the warning just won't show */ }
    setEditing({ invoiceNumber: entry.invoiceNumber, usage });
    setChangeReason("");
    setPeriodChosen(true);
    setForm(formFromSnapshot(entry));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const selectClient = (code) => {
    cancelEdit();
    setClientCode(code);
    localStorage.setItem(LAST_CLIENT_KEY, code);
    setForm(emptyForm(code));
  };

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const changeInvoiceDate = (event) => {
    const value = event.target.value;
    setForm((current) => ({ ...current, invoiceDate: value, accountingPeriod: !periodChosen && value ? value.slice(0, 7) : current.accountingPeriod }));
  };
  const changePeriod = (event) => { setPeriodChosen(true); setForm((current) => ({ ...current, accountingPeriod: event.target.value })); };

  // The P&L, Client Profitability and GST control all use the ACCOUNTING PERIOD, not the invoice date.
  const periodDiffers = Boolean(form.invoiceDate && form.accountingPeriod && form.invoiceDate.slice(0, 7) !== form.accountingPeriod);
  // If the description names a month ("Jul-2026") that is not the accounting period, say so.
  const namedMonths = descriptionMonths(form.reason);
  const descriptionDiffers = namedMonths.length > 0 && !namedMonths.includes(form.accountingPeriod);
  const monthName = (value) => (value ? new Date(`${value.slice(0, 7)}-01T00:00:00Z`).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" }) : "");

  const submit = async (event) => {
    event.preventDefault();
    setError(""); setMessage("");
    if (descriptionDiffers && !window.confirm(
      `The description mentions ${namedMonths.map(monthName).join(" / ")}, but this invoice will be counted in the ${monthName(form.accountingPeriod)} P&L.\n\n` +
      `Keep ${monthName(form.accountingPeriod)}? Press Cancel to change the Accounting Period or the description.`
    )) return;
    if (periodDiffers && !descriptionDiffers && !window.confirm(
      `This invoice is dated ${monthName(form.invoiceDate)} but will be counted in the ${monthName(form.accountingPeriod)} P&L, Client Profitability and GST control.

` +
      `Keep ${monthName(form.accountingPeriod)}? Press Cancel to change the Accounting Period.`
    )) return;
    setSaving(true);
    try {
      if (editing) {
        const { fields, changes, renumbered } = await updateManualInvoiceSnapshot({ originalInvoiceNumber: editing.invoiceNumber, input: form, changeReason });
        setMessage(`${fields.invoiceNumber} corrected${renumbered ? ` (renumbered from ${editing.invoiceNumber})` : ""}: ${changes.filter((c) => c.field !== "Reason" && c.field !== "Expected receipt").map((c) => `${c.field} ${c.from ?? "—"} → ${c.to ?? "—"}`).join("; ") || "details updated"}. Expected receipt: ${money(fields.totals.expectedReceipt)}.`);
        cancelEdit();
      } else {
        const saved = await createManualInvoiceSnapshot(form);
        setMessage(`${saved.invoiceNumber} recorded in the ${monthName(saved.accountingPeriod)} P&L (invoice dated ${saved.invoiceDate}). Expected receipt: ${money(saved.totals.expectedReceipt)}.`);
        setPeriodChosen(false);
        setForm(emptyForm(clientCode));
      }
      await refresh(clientCode);
    } catch (err) {
      setError(err.message || "Could not save this entry.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Management Finance</div>
            <h1 className="page-title">Manual Invoice Entry</h1>
            <p className="page-copy">
              For historical months where what was actually invoiced doesn't match what today's formulas would
              produce — e.g. a recovery rate that's since been corrected. Enter the real figures from what was
              sent to the client; this bypasses the automated calculation entirely and feeds Accounts Receivable
              and WHT/GST Control exactly like a normal approval would. It never overwrites an automated approval. A saved entry can be corrected with Edit — a reason is required and the before/after is kept in the corrections log.
            </p>
          </div>
          <div className="field" style={{ minWidth: 200 }}>
            <label>Client</label>
            <select className="input" value={clientCode} onChange={(e) => selectClient(e.target.value)}>
              {CLIENT_LIST.map((code) => <option key={code} value={code}>{code}</option>)}
            </select>
          </div>
        </header>

        {(message || error) && <div className={`management-alert ${error ? "error" : "success"}`}>{error || message}</div>}

        <section className="panel">
          <div className="panel-header"><h2 className="panel-title">{editing ? `Correcting ${editing.invoiceNumber}` : "Enter an Invoice"}</h2></div>
          <form className="panel-body" onSubmit={submit}>
            {editing && editing.usage.allocationCount > 0 && (
              <div className="management-alert error">
                {money(editing.usage.allocatedAmount)} of receipts ({editing.usage.allocationCount}) are already applied to this invoice. You can correct the figures, but the invoice number is locked, and Accounts Receivable will recalculate what is outstanding.
              </div>
            )}
            <div className="form-grid">
              <div className="field"><label>Invoice Number *</label>
                <input className="input" value={form.invoiceNumber} onChange={update("invoiceNumber")} placeholder="e.g. IIL-27-0009" />
              </div>
              <div className="field"><label>Invoice Date *</label>
                <input className="input" type="date" value={form.invoiceDate} onChange={changeInvoiceDate} />
              </div>
              <div className="field"><label>Accounting Period (P&amp;L month) *</label>
                <input className="input" type="month" value={form.accountingPeriod} onChange={changePeriod} />
                <p className="page-copy" style={{ margin: "4px 0 0", fontSize: 12, color: periodDiffers ? "#c0392b" : undefined, fontWeight: periodDiffers ? 600 : undefined }}>
                  {periodDiffers
                    ? `Dated ${monthName(form.invoiceDate)} but will appear in the ${monthName(form.accountingPeriod)} P&L.`
                    : `This invoice will appear in the ${monthName(form.accountingPeriod)} P&L.`}
                </p>
              </div>
              <div className="field"><label>WHT Model (informational)</label>
                <select className="input" value={form.whtModelUsed} onChange={update("whtModelUsed")}>
                  <option value="">Not specified</option>
                  <option value={WHT_MODELS.SERVICE_CHARGES_PLUS_GST}>Service Charges + GST</option>
                  <option value={WHT_MODELS.GROSS_INVOICE_WITH_RECOVERY}>Gross Invoice with Recovery</option>
                </select>
              </div>
              <div className="field"><label>Service Charge Subtotal</label>
                <input className="input" type="number" value={form.serviceChargeSubtotal} onChange={update("serviceChargeSubtotal")} />
              </div>
              <div className="field"><label>GST Amount</label>
                <input className="input" type="number" value={form.gstAmount} onChange={update("gstAmount")} />
              </div>
              <div className="field"><label>Gross Amount *</label>
                <input className="input" type="number" value={form.grossAmount} onChange={update("grossAmount")} />
              </div>
              <div className="field"><label>WHT Amount (actual deducted)</label>
                <input className="input" type="number" value={form.whtAmount} onChange={update("whtAmount")} />
              </div>
              <div className="field"><label>GST Withheld Amount</label>
                <input className="input" type="number" value={form.gstWithheldAmount} onChange={update("gstWithheldAmount")} />
              </div>
              <div className="field"><label>Recovery Add-back Embedded (optional)</label>
                <input className="input" type="number" value={form.recoveryAddBack} onChange={update("recoveryAddBack")} placeholder="e.g. actual rate used at the time" />
              </div>
            </div>
            {descriptionDiffers && (
              <div className="management-alert error">
                The description mentions <strong>{namedMonths.map(monthName).join(" / ")}</strong>, but the Accounting Period is <strong>{monthName(form.accountingPeriod)}</strong> — this invoice will appear in the {monthName(form.accountingPeriod)} P&amp;L.
              </div>
            )}
            <div className="field"><label>Reason this is manual *</label>
              <textarea className="textarea" value={form.reason} onChange={update("reason")}
                placeholder="e.g. July/Aug invoices used a 7.4807% recovery rate assuming 6% WHT — before the rate was corrected to 7% / 8.8376%. Entering actual figures instead of recalculating." />
            </div>
            {editing && (
              <div className="field"><label>Reason for this correction *</label>
                <textarea className="textarea" value={changeReason} onChange={(e) => setChangeReason(e.target.value)}
                  placeholder="e.g. Wrong service charge typed — corrected to the amount on the PDF sent to the client." />
              </div>
            )}
            <div className="button-row">
              <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? "Saving…" : editing ? "Save Correction" : "Save Entry"}</button>
              {editing && <button className="btn btn-muted" type="button" onClick={cancelEdit}>Cancel</button>}
            </div>
          </form>
        </section>

        <section className="panel">
          <div className="panel-header"><h2 className="panel-title">{clientCode} — Manual Entries</h2></div>
          <div className="panel-body">
            {loading ? <p className="page-copy">Loading…</p> : (
              <table className="data-table">
                <thead>
                  <tr><th>Invoice</th><th>Period</th><th>Gross</th><th>WHT</th><th>GST Withheld</th><th>Expected Receipt</th><th>Reason</th><th /></tr>
                </thead>
                <tbody>
                  {entries.sort((a, b) => (b.accountingPeriod || "").localeCompare(a.accountingPeriod || "")).map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.invoiceNumber}</td>
                      <td>{entry.accountingPeriod}{String(entry.invoiceDate || "").slice(0, 7) !== entry.accountingPeriod && <div className="page-copy" style={{ fontSize: 12 }}>dated {entry.invoiceDate}</div>}</td>
                      <td>{money(entry.totals?.grossAmount)}</td>
                      <td>{money(entry.totals?.whtAmount)}</td>
                      <td>{money(entry.totals?.gstWithheldAmount)}</td>
                      <td>{money(entry.totals?.expectedReceipt)}</td>
                      <td>{entry.manualEntryReason}{entry.lastEditReason && <div className="page-copy" style={{ fontSize: 12 }}>Corrected {entry.editCount > 1 ? `${entry.editCount} times` : "once"} — latest: {entry.lastEditReason}</div>}</td>
                      <td><button className="btn btn-muted" type="button" onClick={() => startEdit(entry)}>Edit</button></td>
                    </tr>
                  ))}
                  {!entries.length && <tr><td colSpan={8}>No manual entries yet for {clientCode}.</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
